import { getSchemaTab, SchemaTab, setSchemaTab } from 'app/schema';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import { all, put, select, takeLatest } from 'redux-saga/effects';
import { showMessage } from '../messages/actions.js';
import {
  type ApiIndexEndPayload,
  type ApiIndexErrorPayload,
  type ApiIndexStartPayload,
  apiIndexEnd,
  apiIndexError,
  apiIndexStart,
  type RouterLocationChangedPayload,
  routerLocationChanged
} from './slice';

export function* handleApiIndexStart({ payload: { shapeName } }: { payload: ApiIndexStartPayload }) {
  yield put(showMessage('info', `Started indexing ${shapeName}`, 3000));
}

function summarizeErrors(errors: string[]) {
  const suffix = 'View the API Indexing Log for more information.';

  if (errors.length === 1) {
    return `Error occurred while indexing: "${errors[0]}" ${suffix}`;
  }

  return `${errors.length} errors occurred while indexing. ${suffix}`;
}

export function* handleApiIndexEnd({
  payload: { shapeName, successful, failed, duration, deleted, deleteFailed, errors }
}: {
  payload: ApiIndexEndPayload;
}) {
  if (errors.length > 0) {
    yield put(showMessage('error', summarizeErrors(errors)));
  } else {
    const formattedDuration = formatDuration(intervalToDuration({ start: 0, end: duration }));
    let message = `Finished indexing ${shapeName} in ${
      formattedDuration || 'less than a second'
    } (${successful} items indexed`;
    if (failed > 0) {
      message += `, ${failed} failed`;
    }

    if (deleted > 0) {
      message += `, ${deleted} deleted`;
    }

    if (deleteFailed > 0) {
      message += `, ${deleteFailed} failed to delete`;
    }

    message += ')';
    yield put(showMessage('info', message, 5000));
  }
}

export function* handleApiIndexError({ payload: { shapeName, message } }: { payload: ApiIndexErrorPayload }) {
  yield put(showMessage('error', `Error indexing ${shapeName}: ${message}`));
}

export function* handleRouterLocationChanged({ payload }: { payload: RouterLocationChangedPayload }) {
  if (payload.route?.startsWith('/project/:projectId/branch/:branch/api')) {
    const schemaTab: SchemaTab = yield select(getSchemaTab);

    if (
      payload.query &&
      payload.query.view !== schemaTab &&
      Object.values(SchemaTab).includes(payload.query.view as SchemaTab)
    ) {
      yield put(setSchemaTab(payload.query.view as SchemaTab));
    }
  }
}

export default function* root() {
  yield all([
    takeLatest(apiIndexStart, handleApiIndexStart),
    takeLatest(apiIndexEnd, handleApiIndexEnd),
    takeLatest(apiIndexError, handleApiIndexError),
    takeLatest(routerLocationChanged, handleRouterLocationChanged)
  ]);
}
